<template>
  <div class="b5-wrapper" ref="el" :class="{ 'is-visible': isVisible }">

    <!-- ── Citation ───────────────────────────────── -->
    <div class="b5-quote-block">
      <div class="b5-quote-inner">
        <blockquote class="b5-quote" v-html="t('b5_quote')"></blockquote>
        <p class="b5-quote-author">Bamboo Sushi Albi</p>
      </div>
    </div>

    <!-- ── Statistiques ───────────────────────────── -->
    <div class="b5-stats">
      <div class="b5-stat">
        <div class="b5-stat-num">100%</div>
        <div class="b5-stat-label">{{ t('b5_stat1_label') }}</div>
      </div>
      <div class="b5-stat-sep"></div>
      <div class="b5-stat">
        <div class="b5-stat-num">+15</div>
        <div class="b5-stat-label">{{ t('b5_stat2_label') }}</div>
      </div>
      <div class="b5-stat-sep"></div>
      <div class="b5-stat">
        <div class="b5-stat-num">40+</div>
        <div class="b5-stat-label">{{ t('b5_stat3_label') }}</div>
      </div>
      <div class="b5-stat-sep"></div>
      <div class="b5-stat">
        <div class="b5-stat-num">4,7 <span class="b5-star">★</span></div>
        <div class="b5-stat-label">{{ t('b5_stat4_label') }}</div>
      </div>
    </div>

    <!-- ── Slider ───────────────────────────────────── -->
    <div class="b5-slider-block">
      <div class="b5-slider">

        <div
          v-for="(slide, i) in slides"
          :key="slide.src"
          class="b5-slide"
          :style="{ opacity: i === activeSlide ? 1 : 0 }"
        >
          <img :src="slide.src" :alt="slide.label" class="b5-slide-img" :style="{ objectPosition: slide.pos }" />
          <div class="b5-slide-overlay">
            <span class="b5-slide-ref">{{ slide.ref }}</span>
            <span class="b5-slide-label">{{ slide.label }}</span>
          </div>
        </div>

        <!-- Dots -->
        <div class="b5-dots">
          <button
            v-for="(_, i) in slides"
            :key="i"
            class="b5-dot"
            :class="{ active: i === activeSlide }"
            @click="goTo(i)"
            :aria-label="`Slide ${i + 1}`"
          />
        </div>

        <!-- Flèches -->
        <button class="b5-arrow b5-arrow-prev" @click="goTo((activeSlide - 1 + slides.length) % slides.length)" :aria-label="t('b5_prev')">&#8249;</button>
        <button class="b5-arrow b5-arrow-next" @click="goTo((activeSlide + 1) % slides.length)" :aria-label="t('b5_next')">&#8250;</button>

      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useReveal } from '@/composables/useReveal'
import { useLocale } from '@/i18n'

const { el, isVisible } = useReveal()
const { t } = useLocale()

const slides = computed(() => [
  { src: '/img/KLS8.jpg',    ref: 'SP · Dragon',      label: t('b5_slide1_label'),  pos: 'center center' },
  { src: '/img/KLS1.jpg',    ref: 'SP · Spider',      label: t('b5_slide2_label'),  pos: 'center top'    },
  { src: '/img/KLS2.jpg',    ref: 'SP · Rainbow',     label: t('b5_slide3_label'),  pos: 'center top'    },
  { src: '/img/KLS3.jpg',    ref: 'SP · Volcano',     label: t('b5_slide4_label'),  pos: 'center center' },
  { src: '/img/KLS4.jpg',    ref: 'SP · Tropical',    label: t('b5_slide5_label'),  pos: 'center top'    },
  { src: '/img/KLS5.jpg',    ref: 'KS · California',  label: t('b5_slide6_label'),  pos: 'center center' },
  { src: '/img/KLS6.jpg',    ref: 'KSK · California', label: t('b5_slide7_label'),  pos: 'center center' },
  { src: '/img/C3.jpg', ref: 'C3 · Chirashis variés', label: t('b5_slide8_label'),  pos: 'center center' },
])

const activeSlide = ref(0)
let timer = null

function goTo(index) {
  activeSlide.value = index
}

onMounted(() => {
  timer = setInterval(() => {
    activeSlide.value = (activeSlide.value + 1) % slides.value.length
  }, 4500)
})

onUnmounted(() => clearInterval(timer))
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;1,400&display=swap');

/* ── Animation reveal ────────────────────────────── */
.b5-wrapper {
  opacity: 0;
  transform: translateY(40px);
  transition: opacity 0.75s ease, transform 0.75s ease;
}
.b5-wrapper.is-visible {
  opacity: 1;
  transform: translateY(0);
}

/* ── Wrapper ─────────────────────────────────────── */
.b5-wrapper {
  width: 100%;
  align-self: stretch;
  background: linear-gradient(160deg, #1a1912 0%, #13130e 55%, #111110 100%);
  border-top: 1px solid rgba(200, 160, 85, 0.2);
  position: relative;
  overflow: hidden;
}

.b5-wrapper::before {
  content: '匠';
  position: absolute;
  left: 50%;
  transform: translateX(-65%);
  top: 3rem;
  font-size: clamp(280px, 35vw, 480px);
  color: rgba(200, 160, 85, 0.04);
  font-family: 'Hiragino Mincho Pro', 'Yu Mincho', 'MS Mincho', serif;
  line-height: 1;
  pointer-events: none;
  user-select: none;
  z-index: 0;
}

/* ── Contenu au-dessus du kanji ──────────────────── */
.b5-quote-block,
.b5-stats,
.b5-image-block {
  position: relative;
  z-index: 1;
}

/* ── Citation ────────────────────────────────────── */
.b5-quote-block {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 5rem 2rem 4rem;
}

.b5-quote-inner {
  max-width: 780px;
  text-align: center;
}

.b5-quote {
  font-family: 'Playfair Display', serif;
  font-size: clamp(1.4rem, 3vw, 2rem);
  font-weight: 400;
  font-style: italic;
  color: #ede8df;
  line-height: 1.7;
  margin: 0 0 1.5rem;
  letter-spacing: 0.01em;
}

.b5-quote em {
  font-style: italic;
  color: #c8a055;
  font-weight: 400;
}

.b5-quote-author {
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: #706a60;
  margin: 0;
}

/* ── Statistiques ────────────────────────────────── */
.b5-stats {
  display: flex;
  align-items: center;
  justify-content: center;
  border-top: 1px solid rgba(200, 160, 85, 0.12);
  border-bottom: 1px solid rgba(200, 160, 85, 0.12);
  flex-wrap: wrap;
}

.b5-stat {
  flex: 1 1 160px;
  padding: 2.5rem 1.5rem;
  text-align: center;
}

.b5-stat-sep {
  width: 1px;
  height: 60px;
  background: rgba(200, 160, 85, 0.15);
  flex-shrink: 0;
}

.b5-stat-num {
  font-family: 'Playfair Display', serif;
  font-size: clamp(2rem, 4vw, 2.8rem);
  font-weight: 400;
  color: #c8a055;
  line-height: 1;
  margin-bottom: 0.6rem;
}

.b5-star {
  font-size: 0.65em;
  vertical-align: middle;
}

.b5-stat-label {
  font-size: 0.62rem;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #706a60;
}

/* ── Slider ──────────────────────────────────────── */
.b5-slider-block {
  padding: 4rem 2rem 5rem;
  display: flex;
  justify-content: center;
}

.b5-slider {
  position: relative;
  max-width: 720px;
  width: 100%;
  height: 520px;
  border: 1px solid rgba(200, 160, 85, 0.2);
  overflow: hidden;
  background: #111;
}

.b5-slide {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  transition: opacity 1.2s ease;
}

.b5-slide-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
}

.b5-slide-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 2rem 2rem 1.5rem;
  background: linear-gradient(to top, rgba(14, 14, 10, 0.92) 0%, rgba(14, 14, 10, 0.4) 60%, transparent 100%);
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.b5-slide-ref {
  font-size: 0.58rem;
  font-weight: 600;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: #c8a055;
}

.b5-slide-label {
  font-family: 'Playfair Display', serif;
  font-size: 1.05rem;
  font-style: italic;
  color: #ede8df;
  letter-spacing: 0.02em;
}

/* ── Dots ────────────────────────────────────────── */
.b5-dots {
  position: absolute;
  bottom: 14px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 7px;
  z-index: 10;
}

.b5-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.25);
  border: none;
  cursor: pointer;
  padding: 0;
  transition: background 0.3s, transform 0.3s;
}

.b5-dot.active {
  background: #c8a055;
  transform: scale(1.3);
}

/* ── Flèches ─────────────────────────────────────── */
.b5-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.45);
  border: 1px solid rgba(200, 160, 85, 0.2);
  color: #c8a055;
  font-size: 1.8rem;
  line-height: 1;
  width: 40px;
  height: 40px;
  cursor: pointer;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.25s, border-color 0.25s;
  padding: 0;
}

.b5-arrow:hover {
  background: rgba(200, 160, 85, 0.25);
  border-color: #c8a055;
}

.b5-arrow-prev { left: 12px; }
.b5-arrow-next { right: 12px; }

/* ── Responsive ──────────────────────────────────── */
@media (max-width: 768px) {
  .b5-stat-sep {
    display: none;
  }

  .b5-stats {
    gap: 0;
  }

  .b5-stat {
    flex: 1 1 140px;
    padding: 1.75rem 1rem;
  }

  .b5-slider {
    height: 340px;
  }

  .b5-arrow {
    width: 32px;
    height: 32px;
    font-size: 1.4rem;
  }

  .b5-quote-block {
    padding: 3.5rem 1.5rem 3rem;
  }
}
</style>
